import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import * as React from 'react';
import { useLingui } from '@lingui/react';
import Icon from 'components/Icon';
import { classNames } from 'utils/ui/css';
import { endSession, isSessionActive, userSession } from 'state/session';
import { showToast } from 'state/toast';
import { logout } from 'utils/auth';
import { user } from 'utils/auth/user';

const UserMenu: React.FC = () => {
  const { _ } = useLingui();
  const [showUserMenu, setShowUserMenu] = React.useState(false);
  userSession().useState();
  const dealerName = user.name;
  const dealerNameInitials: string =
    dealerName &&
    dealerName
      .split(' ')
      .map((s: string) => s.charAt(0))
      .join('');

  const clearSessionAndLogout = async (): Promise<void> => {
    try {
      setShowUserMenu(false);
      const session = userSession().get();
      if (isSessionActive(session)) {
        await endSession({ isLogout: true, keepAlive: true });
      }
      await logout();
    } catch (e) {
      showToast({ title: _(t`Failed to logout`), type: 'error', notificationId: 'ERROR_LOGOUT' });
      setShowUserMenu(true);
      console.error('Error while logging out', e);
    }
  };

  return (
    <div className="relative inline-block text-left">
      <button
        type="button"
        className="flex size-8 cursor-pointer items-center justify-center rounded-full bg-white"
        onClick={() => {
          setShowUserMenu(!showUserMenu);
        }}
        aria-expanded={showUserMenu}
        aria-haspopup="true"
        aria-controls="dropdown-menu"
        aria-label={_(t`User menu`)}
        id="menu-button"
      >
        {dealerNameInitials ? (
          // AVS-4423: initials derived from a real employee's name. Masking costs only these two
          // glyphs — `data-e3d-mask` matches this <span> alone, not the <button> that contains it.
          <span
            className={classNames('text-sm text-teal-700 hover:text-teal-600-10', showUserMenu && 'text-teal-600-10')}
            data-e3d-mask
          >
            {dealerNameInitials}
          </span>
        ) : (
          <Icon className="ex3d-icon--avatar size-2/3 transition-colors" />
        )}
      </button>
      <div
        className={classNames(
          'focus:outline-hidden transition-all absolute right-0 z-20 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black/5',
          showUserMenu ? 'opacity-100' : 'opacity-0 pointer-events-none'
        )}
        role="menu"
        aria-orientation="vertical"
        aria-labelledby="menu-button"
        id="dropdown-menu"
        tabIndex={-1}
      >
        <div className="py-1" role="none">
          <span
            className="block cursor-pointer px-4 py-2 text-sm text-gray-700 transition-colors hover:text-gray-300"
            role="menuitem"
            tabIndex={-1}
            id="menu-item-0"
            data-testid="menu-item-logout"
            onClick={clearSessionAndLogout}
          >
            <Trans>Logout</Trans>
          </span>
        </div>
      </div>
    </div>
  );
};

export default UserMenu;
